// ============================================================
// 项目部署·视频产物（DW-P2-8）
// - 录制对象：3D 场景画布（复用 sceneImageExportRegistry.capture 逐帧采样 → 2D 合成画布）
//   * capture 由 SceneCanvas 注册（内部触发渲染 + WebGL readback），避免 preserveDrawingBuffer 空白帧问题
// - 视频：合成画布 captureStream + MediaRecorder（优先 MP4/H.264，回退 WebM；手动 requestFrame 精确控帧）
// - GIF：同帧采样 → web-safe 216 色量化 → omggif GifWriter（纯前端编码）
// - 水印：免费/临时档烧录（斜纹 + 左下角 logo/文字，样式与 stampWatermark 一致）
// - 服务端仅轻量记录（release kind='video' 计额度 + 审计 video_export）
// ============================================================
import { GifWriter } from 'omggif';
import { sceneImageExportRegistry } from '../stores/sceneImageExportStore';

export type DeployVideoFormat = 'mp4' | 'webm' | 'gif';

export interface DeployVideoOptions {
  name?: string;
  format: DeployVideoFormat;
  durationSec: number;
  fps?: number;
  /** 传非空值即烧录水印（免费/临时档） */
  watermark?: { text?: string; logoUrl?: string } | null;
  /** 采样宽度上限（默认 1920；GIF 再压到 GIF_MAX_WIDTH） */
  maxWidth?: number;
  onProgress?: (elapsedSec: number) => void;
}

export interface DeployVideoResult {
  blob: Blob;
  filename: string;
  format: DeployVideoFormat;
  mime: string;
  width: number;
  height: number;
  durationSec: number;
  fps: number;
}

export const VIDEO_FPS_OPTIONS = [24, 30];
export const GIF_MAX_WIDTH = 640;
export const GIF_MAX_DURATION_SEC = 8;
export const GIF_FPS = 8;

/** 时长收敛：1..maxSec（maxSec 非法时回退 15） */
export function clampVideoDuration(sec: number, maxSec: number): number {
  const max = Math.max(1, Math.trunc(Number(maxSec) || 15));
  return Math.min(max, Math.max(1, Math.trunc(Number(sec) || 1)));
}

/** 选择可用的录制容器：优先 MP4/H.264，回退 WebM/VP9 → WebM */
export function pickVideoFormat(isSupported: (t: string) => boolean): {
  format: 'mp4' | 'webm';
  mime: string;
  ext: string;
} {
  const candidates: { format: 'mp4' | 'webm'; mime: string; ext: string }[] = [
    { format: 'mp4', mime: 'video/mp4;codecs=avc1.42E01E', ext: 'mp4' },
    { format: 'mp4', mime: 'video/mp4', ext: 'mp4' },
    { format: 'webm', mime: 'video/webm;codecs=vp9', ext: 'webm' },
    { format: 'webm', mime: 'video/webm', ext: 'webm' },
  ];
  for (const c of candidates) {
    try {
      if (isSupported(c.mime)) return c;
    } catch {
      /* isTypeSupported 异常忽略 */
    }
  }
  return candidates[3];
}

/** 码率（约 0.07 bit/像素/帧，2~16 Mbps） */
export function videoBitrateFor(width: number, height: number, fps: number): number {
  const raw = Math.round(Math.max(1, width) * Math.max(1, height) * Math.max(1, fps) * 0.07);
  return Math.min(16_000_000, Math.max(2_000_000, raw));
}

export function buildVideoFilename(
  name: string,
  width: number,
  height: number,
  ext: string,
): string {
  const safe =
    String(name || '场景')
      .replace(/[\\/:*?"<>|\s]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || '场景';
  return `${safe}-${width}x${height}.${ext}`;
}

// ---------------- GIF 调色板（web-safe 216 色网格） ----------------

const LEVELS = [0, 51, 102, 153, 204, 255];

export function webSafePalette(): number[] {
  const palette: number[] = [];
  for (let r = 0; r < 6; r++) {
    for (let g = 0; g < 6; g++) {
      for (let b = 0; b < 6; b++) {
        palette.push((LEVELS[r] << 16) | (LEVELS[g] << 8) | LEVELS[b]);
      }
    }
  }
  return palette;
}

/** omggif 要求调色板长度为 2 的幂（216 → 补黑到 256） */
export function gifPalette256(): number[] {
  const p = webSafePalette();
  while (p.length < 256) p.push(0);
  return p;
}

/** RGB → 216 色网格索引（独立通道就近取整，网格完备即最近邻） */
export function webSafeIndex(r: number, g: number, b: number): number {
  const lr = Math.min(5, Math.max(0, Math.round(r / 51)));
  const lg = Math.min(5, Math.max(0, Math.round(g / 51)));
  const lb = Math.min(5, Math.max(0, Math.round(b / 51)));
  return lr * 36 + lg * 6 + lb;
}

function quantizeRgba(data: Uint8ClampedArray): Uint8Array {
  const n = Math.floor(data.length / 4);
  const out = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    out[i] = webSafeIndex(data[i * 4], data[i * 4 + 1], data[i * 4 + 2]);
  }
  return out;
}

// ---------------- 采样与合成 ----------------

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('帧解码失败'));
    img.src = src;
  });
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function targetSize(maxWidth: number): { width: number; height: number } {
  const canvas = sceneImageExportRegistry.getCanvas?.() ?? null;
  const naturalW = Math.max(1, canvas?.width || 1280);
  const naturalH = Math.max(1, canvas?.height || 720);
  const cap = Math.max(160, Math.trunc(maxWidth) || 1920);
  if (naturalW <= cap) return { width: naturalW, height: naturalH };
  const width = cap;
  return { width, height: Math.max(2, Math.round((naturalH / naturalW) * width / 2) * 2) };
}

/** 水印烧录（与 stampWatermark 同款式：斜纹 + 左下角 logo/文字） */
export function drawFrameWatermark(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  text: string,
  logo?: HTMLImageElement | null,
): void {
  const fontSize = Math.max(18, Math.round(width * 0.028));
  ctx.save();
  ctx.font = `600 ${fontSize}px sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.16)';
  ctx.textBaseline = 'middle';
  ctx.translate(width / 2, height / 2);
  ctx.rotate((-30 * Math.PI) / 180);
  const stepX = width * 0.42;
  const stepY = height * 0.22;
  for (let y = -height; y <= height; y += stepY) {
    for (let x = -width; x <= width; x += stepX) {
      ctx.fillText(text, x, y);
    }
  }
  ctx.restore();

  const pad = Math.round(width * 0.012);
  const logoSize = Math.round(width * 0.02);
  let x = pad;
  if (logo) {
    ctx.globalAlpha = 0.9;
    ctx.drawImage(logo, pad, height - pad - logoSize, logoSize, logoSize);
    ctx.globalAlpha = 1;
    x = pad + logoSize + 8;
  }
  ctx.font = `500 ${Math.max(14, Math.round(width * 0.011))}px sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.fillText(text, x, height - pad - logoSize / 2 + 4);
}

interface SamplingHandle {
  compose: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  fps: number;
  total: number;
}

async function setupSampling(opts: DeployVideoOptions, width: number, height: number, fps: number): Promise<SamplingHandle> {
  const compose = document.createElement('canvas');
  compose.width = width;
  compose.height = height;
  const ctx = compose.getContext('2d');
  if (!ctx) throw new Error('无法创建合成画布');
  ctx.fillStyle = '#0a0a1a';
  ctx.fillRect(0, 0, width, height);
  const total = Math.max(1, Math.round(opts.durationSec * fps));
  return { compose, ctx, width, height, fps, total };
}

/** 逐帧采样主循环；onFrame 在每帧合成完成后回调（视频 requestFrame / GIF 取 ImageData） */
async function runSampling(
  opts: DeployVideoOptions,
  s: SamplingHandle,
  onFrame: (index: number) => void,
): Promise<void> {
  const capture = sceneImageExportRegistry.capture;
  if (!capture) throw new Error('当前没有可录制的 3D 画布（请在 3D 场景视图中使用）');
  const logo = opts.watermark?.logoUrl
    ? await loadImage(opts.watermark.logoUrl).catch(() => null)
    : null;
  const wmText = opts.watermark?.text ?? 'DTwince';
  const interval = 1000 / s.fps;
  for (let i = 0; i < s.total; i++) {
    const t0 = performance.now();
    const dataUrl = capture({ width: s.width, height: s.height, format: 'png' });
    if (dataUrl) {
      try {
        const img = await loadImage(dataUrl);
        s.ctx.drawImage(img, 0, 0, s.width, s.height);
      } catch {
        /* 单帧失败沿用上一帧 */
      }
    }
    if (opts.watermark) drawFrameWatermark(s.ctx, s.width, s.height, wmText, logo);
    onFrame(i);
    opts.onProgress?.((i + 1) / s.fps);
    const wait = interval - (performance.now() - t0);
    if (wait > 0) await sleep(wait);
  }
}

// ---------------- 视频（MP4/WebM） ----------------

export async function recordSceneVideo(opts: DeployVideoOptions): Promise<DeployVideoResult> {
  if (typeof MediaRecorder === 'undefined') throw new Error('当前浏览器不支持视频录制（MediaRecorder）');
  const fps = VIDEO_FPS_OPTIONS.includes(Number(opts.fps)) ? Number(opts.fps) : 30;
  const { width, height } = targetSize(opts.maxWidth ?? 1920);
  const s = await setupSampling(opts, width, height, fps);

  const picked = pickVideoFormat((t) => MediaRecorder.isTypeSupported(t));
  const baseStream = s.compose.captureStream(0);
  const track = baseStream.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack;
  const manual = typeof track?.requestFrame === 'function';
  const stream = manual ? baseStream : s.compose.captureStream(fps);

  const chunks: BlobPart[] = [];
  const recorder = new MediaRecorder(stream, {
    mimeType: picked.mime,
    videoBitsPerSecond: videoBitrateFor(width, height, fps),
  });
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };
  const stopped = new Promise<void>((resolve) => {
    recorder.onstop = () => resolve();
  });
  recorder.start(250);
  try {
    await runSampling(opts, s, () => {
      if (manual) track.requestFrame();
    });
  } finally {
    if (recorder.state !== 'inactive') recorder.stop();
  }
  await stopped;
  const blob = new Blob(chunks, { type: picked.mime.split(';')[0] });
  if (blob.size === 0) throw new Error('录制失败：未采集到视频帧');
  return {
    blob,
    filename: buildVideoFilename(opts.name ?? '场景', width, height, picked.ext),
    format: picked.format,
    mime: picked.mime.split(';')[0],
    width,
    height,
    durationSec: opts.durationSec,
    fps,
  };
}

// ---------------- GIF ----------------

export async function recordSceneGif(opts: DeployVideoOptions): Promise<DeployVideoResult> {
  const durationSec = Math.min(GIF_MAX_DURATION_SEC, clampVideoDuration(opts.durationSec, GIF_MAX_DURATION_SEC));
  const fps = GIF_FPS;
  const natural = targetSize(opts.maxWidth ?? GIF_MAX_WIDTH);
  const { width, height } = targetSize(Math.min(GIF_MAX_WIDTH, natural.width));
  const s = await setupSampling({ ...opts, durationSec }, width, height, fps);

  const frames: Uint8Array[] = [];
  await runSampling({ ...opts, durationSec }, s, () => {
    frames.push(quantizeRgba(s.ctx.getImageData(0, 0, s.width, s.height).data));
  });
  if (frames.length === 0) throw new Error('录制失败：未采集到 GIF 帧');

  const palette = gifPalette256();
  const buf = new Uint8Array(s.width * s.height * frames.length * 2 + 65_536);
  const writer = new GifWriter(buf, s.width, s.height, { palette, loop: 0 });
  const delay = Math.max(2, Math.round(100 / fps));
  for (const frame of frames) {
    writer.addFrame(0, 0, s.width, s.height, frame, { delay, disposal: 2 });
  }
  const len = writer.end();
  const blob = new Blob([buf.slice(0, len)], { type: 'image/gif' });
  return {
    blob,
    filename: buildVideoFilename(opts.name ?? '场景', s.width, s.height, 'gif'),
    format: 'gif',
    mime: 'image/gif',
    width: s.width,
    height: s.height,
    durationSec,
    fps,
  };
}

/** 统一入口：按格式录制（视频/GIF），返回 Blob 与元信息 */
export async function recordDeployVideo(opts: DeployVideoOptions): Promise<DeployVideoResult> {
  if (opts.format === 'gif') return recordSceneGif(opts);
  return recordSceneVideo(opts);
}

/** Blob 下载 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// 开发/测试桥（e2e 用）：真实浏览器内直接调用录制服务
if (import.meta.env?.DEV && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__deployVideoExport = {
    recordSceneVideo,
    recordSceneGif,
    recordDeployVideo,
    pickVideoFormat,
    clampVideoDuration,
    videoBitrateFor,
    buildVideoFilename,
    webSafeIndex,
  };
}
